import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  IconButton,
  Paper,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import StarIcon from "@mui/icons-material/Star";
import PersonIcon from "@mui/icons-material/Person";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BarChartIcon from "@mui/icons-material/BarChart";
import DescriptionIcon from "@mui/icons-material/Description";
import CloseIcon from "@mui/icons-material/Close";

// import DoctorAvatar from "../../components/DoctorAvatar";

const MOCK_APPOINTMENTS = [
  {
    id: "apt-001",
    doctorId: "dr-bala",
    patientName: "Rahul Kumar",
    patientEmail: "rahul@example.com",
    date: "2026-02-14",
    timeSlot: "10:00 AM",
    status: "history_taken",
    paymentStatus: "paid",
  },
  {
    id: "apt-002",
    doctorId: "dr-bala",
    patientName: "Meena Devi",
    patientEmail: "meena@example.com",
    date: "2026-02-14",
    timeSlot: "11:00 AM",
    status: "booked",
    paymentStatus: "paid",
  },
  {
    id: "apt-003",
    doctorId: "dr-bala",
    patientName: "Suresh Patel",
    patientEmail: "suresh@example.com",
    date: "2026-02-14",
    timeSlot: "2:00 PM",
    status: "completed",
    paymentStatus: "paid",
  },
];

const MOCK_DOCTORS = [
  {
    id: "dr-bala",
    name: "Dr. Balachandra BV",
    specialization: "Pediatrician | Allergy Asthma Specialist",
    roles: ["Educator", "Entrepreneur"],
    qualifications: [
      "MBBS",
      "MD (Pediatrics)",
      "Fellowship (Allergy & Asthma)",
    ],
    experience: 17,
    rating: 4.9,
    reviewCount: 700,
    consultationFee: 800,
    avatar: "BB",
    photo:
      "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400&h=400&fit=crop&crop=face",
    bio: "Dr. Balachandra is a highly experienced Consultant Pediatrician and Allergy\u2013Asthma Specialist with 17 years of clinical expertise. As Co-Founder of Med-Train, Allergy Central, and LinQMD, he is committed to advancing clinical skills, offering preventive-focused care, educating colleagues and communities, and developing innovative health-tech solutions to enhance healthcare delivery.",
    email: "drbala@med-train.com",
    phone: "+91 96112 55112",
    languages: ["English", "Hindi", "Kannada"],
    availability: [
      { day: "Monday", slots: ["10:00 AM", "11:00 AM", "2:00 PM", "3:00 PM"] },
      {
        day: "Wednesday",
        slots: ["10:00 AM", "11:00 AM", "2:00 PM", "3:00 PM"],
      },
      {
        day: "Friday",
        slots: ["10:00 AM", "11:00 AM", "2:00 PM", "3:00 PM", "4:00 PM"],
      },
      { day: "Saturday", slots: ["10:00 AM", "11:00 AM"] },
    ],
    location: "Bangalore",
    hospital: "Chirayu Multi-Speciality Clinic",
    tags: ["Pediatrics", "Allergy", "Asthma", "Preventive Care"],
    achievements: [
      "17+ years of clinical expertise",
      "Trained 700+ Allergy - Asthma Specialists",
      "Co-Founder of Med-Train, Allergy Central & LinQMD",
    ],
    socialLinks: {
      facebook: "https://facebook.com",
      instagram: "https://instagram.com",
      linkedin: "https://linkedin.com",
      whatsapp: "https://wa.me/919611255112",
      youtube: "https://youtube.com",
      podcast: "#",
      blog: "#",
      locationMap: "https://maps.google.com",
      gallery: "#",
    },
    organizations: [
      { name: "Chirayu Multi-Speciality Clinic" },
      { name: "LinQMD" },
      { name: "MedTrain" },
      { name: "Allergy Central" },
    ],
  },
  {
    id: "dr-001",
    name: "Dr. Priya Sharma",
    specialization: "Rheumatology",
    roles: ["Consultant"],
    qualifications: ["MBBS", "MD (Medicine)", "DM (Rheumatology)"],
    experience: 14,
    rating: 4.8,
    reviewCount: 234,
    consultationFee: 800,
    avatar: "PS",
    photo:
      "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200&h=200&fit=crop&crop=face",
    bio: "Dr. Priya Sharma is a senior consultant rheumatologist with over 14 years of experience in treating autoimmune and inflammatory joint diseases. She specializes in Rheumatoid Arthritis, Lupus (SLE), Ankylosing Spondylitis, and Gout management.",
    email: "priya@example.com",
    phone: "+91 98765 43210",
    languages: ["English", "Hindi"],
    availability: [
      { day: "Monday", slots: ["10:00 AM", "11:00 AM", "2:00 PM", "3:00 PM"] },
      { day: "Wednesday", slots: ["10:00 AM", "11:00 AM", "2:00 PM"] },
      {
        day: "Friday",
        slots: ["10:00 AM", "11:00 AM", "2:00 PM", "3:00 PM", "4:00 PM"],
      },
    ],
    location: "New Delhi",
    hospital: "AIIMS Delhi",
    tags: ["RA", "Lupus", "Gout", "Autoimmune"],
    achievements: [
      "14+ years of clinical expertise",
      "Published 30+ research papers",
      "Consultant at AIIMS Delhi",
    ],
    socialLinks: {
      linkedin: "https://linkedin.com",
      whatsapp: "https://wa.me/919876543210",
    },
    organizations: [{ name: "AIIMS Delhi" }],
  },
];

const THEME = {
  teal: "#22B8B2",
  tealSoft: "#BFF3F0",
  text: "#0F172A",
  muted: "#64748B",
  border: "#DCE7E7",
};

const statusConfig = {
  booked: {
    label: "Booked",
    bg: "#EFF6FF",
    color: "#1D4ED8",
  },
  history_taken: {
    label: "History Ready",
    bg: "#FFFBEB",
    color: "#B45309",
  },
  in_progress: {
    label: "In Progress",
    bg: "#F0FDFA",
    color: "#0F766E",
  },
  completed: {
    label: "Completed",
    bg: "#F0FDF4",
    color: "#15803D",
  },
  cancelled: {
    label: "Cancelled",
    bg: "#FEF2F2",
    color: "#B91C1C",
  },
};

const DoctorDashboard = ({ onClose }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState("upcoming");

  const doctor = MOCK_DOCTORS[0];
  const appointments = MOCK_APPOINTMENTS;

  const upcomingAppointments = useMemo(
    () =>
      appointments.filter(
        (a) => a.status !== "completed" && a.status !== "cancelled",
      ),
    [appointments],
  );

  const pastAppointments = useMemo(
    () =>
      appointments.filter(
        (a) => a.status === "completed" || a.status === "cancelled",
      ),
    [appointments],
  );

  const currentList =
    activeTab === "upcoming" ? upcomingAppointments : pastAppointments;

  const todayCount = upcomingAppointments.filter(
    (a) => a.date === "2026-02-14",
  ).length;

  const historyReadyCount = upcomingAppointments.filter(
    (a) => a.status === "history_taken",
  ).length;

  const getInitials = (name) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("");

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F8FAFC" }}>
      {/* Header */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "#fff",
          color: THEME.text,
          borderBottom: "1px solid #E2E8F0",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ px: "0 !important", justifyContent: "space-between" }}>
            <Stack direction="row" spacing={2} alignItems="center">
              {/* <DoctorAvatar
                photo={doctor.photo}
                avatar={doctor.avatar}
                size="w-10 h-10"
                textSize="text-sm"
              /> */}
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  {doctor.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: THEME.muted, textTransform: "uppercase" }}
                >
                  {doctor.specialization}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1.5}>
              <IconButton sx={{ marginBottom: "0rem" }} onClick={onClose}>
                <CloseIcon size="small" sx={{ color: "red" }} />
              </IconButton>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: 0,
                border: "1px solid #E2E8F0",
              }}
            >
              <CardContent>
                <Typography
                  variant="body2"
                  sx={{ color: THEME.muted, fontWeight: 500 }}
                >
                  Today's Appointments
                </Typography>
                <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
                  {todayCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: 0,
                border: "1px solid #E2E8F0",
              }}
            >
              <CardContent>
                <Typography
                  variant="body2"
                  sx={{ color: THEME.muted, fontWeight: 500 }}
                >
                  History Ready
                </Typography>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ mt: 1, color: THEME.teal }}
                >
                  {historyReadyCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: 0,
                border: "1px solid #E2E8F0",
              }}
            >
              <CardContent>
                <Typography
                  variant="body2"
                  sx={{ color: THEME.muted, fontWeight: 500 }}
                >
                  Total Patients
                </Typography>
                <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
                  {doctor.reviewCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: 0,
                border: "1px solid #E2E8F0",
              }}
            >
              <CardContent>
                <Typography
                  variant="body2"
                  sx={{ color: THEME.muted, fontWeight: 500 }}
                >
                  Rating
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: 1 }}
                >
                  <StarIcon sx={{ color: "#FBBF24" }} />
                  <Typography variant="h4" fontWeight={700}>
                    {doctor.rating}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper
          elevation={0}
          sx={{
            display: "inline-flex",
            p: 0.5,
            mb: 3,
            borderRadius: 2,
            border: "1px solid #E2E8F0",
            bgcolor: "#fff",
          }}
        >
          <Button
            onClick={() => setActiveTab("upcoming")}
            sx={{
              textTransform: "none",
              px: 2.5,
              py: 1,
              borderRadius: 2,
              bgcolor: activeTab === "upcoming" ? THEME.teal : "transparent",
              color: activeTab === "upcoming" ? "#fff" : "#475569",
              "&:hover": {
                bgcolor: activeTab === "upcoming" ? THEME.teal : "#F8FAFC",
              },
            }}
          >
            Upcoming ({upcomingAppointments.length})
          </Button>

          <Button
            onClick={() => setActiveTab("history")}
            sx={{
              textTransform: "none",
              px: 2.5,
              py: 1,
              borderRadius: 2,
              bgcolor: activeTab === "history" ? THEME.teal : "transparent",
              color: activeTab === "history" ? "#fff" : "#475569",
              "&:hover": {
                bgcolor: activeTab === "history" ? THEME.teal : "#F8FAFC",
              },
            }}
          >
            Completed ({pastAppointments.length})
          </Button>
        </Paper>

        {/* Appointment Cards */}
        <Stack spacing={2}>
          {currentList.map((apt) => {
            const status = statusConfig[apt.status];

            return (
              <Card
                key={apt.id}
                sx={{
                  borderRadius: 3,
                  boxShadow: 0,
                  border: "1px solid #E2E8F0",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    justifyContent="space-between"
                    alignItems={isMobile ? "flex-start" : "center"}
                    spacing={3}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: "#E2E8F0",
                          color: "#475569",
                          fontWeight: 700,
                        }}
                      >
                        {getInitials(apt.patientName)}
                      </Avatar>

                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {apt.patientName}
                        </Typography>

                        <Typography variant="body2" sx={{ color: THEME.muted }}>
                          {apt.patientEmail}
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                          sx={{ mt: 1, flexWrap: "wrap" }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: THEME.muted }}
                          >
                            {apt.date} at {apt.timeSlot}
                          </Typography>

                          <Chip
                            label={status.label}
                            size="small"
                            sx={{
                              bgcolor: status.bg,
                              color: status.color,
                              fontWeight: 600,
                              fontSize: "0.7rem",
                            }}
                          />
                        </Stack>
                      </Box>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      {apt.status === "history_taken" && (
                        <>
                          <Button
                            variant="outlined"
                            onClick={() =>
                              navigate(`/doctor/patient-history/${apt.id}`)
                            }
                            sx={{
                              textTransform: "none",
                              borderRadius: 2,
                              borderColor: THEME.teal,
                              color: THEME.teal,
                            }}
                          >
                            View History
                          </Button>

                          <Button
                            variant="contained"
                            onClick={() => navigate(`/meet/${apt.id}`)}
                            sx={{
                              textTransform: "none",
                              borderRadius: 2,
                              bgcolor: THEME.teal,
                              "&:hover": {
                                bgcolor: "#1ca39d",
                              },
                            }}
                          >
                            Start Meet
                          </Button>
                        </>
                      )}

                      {apt.status === "booked" && (
                        <Typography
                          variant="body2"
                          sx={{ color: "#94A3B8", fontStyle: "italic" }}
                        >
                          Waiting for patient history...
                        </Typography>
                      )}

                      {apt.status === "completed" && (
                        <Button
                          variant="outlined"
                          sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            borderColor: "#E2E8F0",
                            color: "#475569",
                          }}
                        >
                          View Summary
                        </Button>
                      )}
                    </Stack>
                  </Stack>

                  {apt.status === "history_taken" && (
                    <Box
                      sx={{
                        mt: 3,
                        pt: 3,
                        borderTop: "1px solid #F1F5F9",
                      }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: "#FFFBEB",
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 1 }}
                        >
                          <DescriptionIcon
                            sx={{ fontSize: 18, color: "#D97706" }}
                          />
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ color: "#B45309" }}
                          >
                            AI History Summary Available
                          </Typography>
                        </Stack>

                        <Typography variant="body2" sx={{ color: "#D97706" }}>
                          Patient reported symmetrical joint pain in small
                          joints (fingers, wrists) with morning stiffness
                          lasting &gt;60 minutes. Duration: 3 months. No red
                          flags detected. Possible RA pathway.
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>

        {currentList.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              py: 8,
              textAlign: "center",
              borderRadius: 3,
              border: "1px solid #E2E8F0",
              bgcolor: "#fff",
            }}
          >
            <Typography sx={{ color: "#94A3B8" }}>
              No {activeTab} appointments
            </Typography>
          </Paper>
        )}

        {/* Quick Actions */}
        <Card
          sx={{
            mt: 4,
            borderRadius: 3,
            boxShadow: 0,
            border: "1px solid #E2E8F0",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
              Quick Actions
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper
                  onClick={() => navigate(`/doctor/${doctor.id}`)}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #E2E8F0",
                    cursor: "pointer",
                    "&:hover": { borderColor: "#7DD3C7" },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: "#F0FDFA",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PersonIcon sx={{ color: "#0F766E" }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Edit Profile
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                        Update your public profile
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #E2E8F0",
                    cursor: "pointer",
                    "&:hover": { borderColor: "#7DD3C7" },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: "#EFF6FF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CalendarMonthIcon sx={{ color: "#2563EB" }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Manage Slots
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                        Update availability
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #E2E8F0",
                    cursor: "pointer",
                    "&:hover": { borderColor: "#7DD3C7" },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: "#F5F3FF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <BarChartIcon sx={{ color: "#7C3AED" }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Earnings
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                        View payment history
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default DoctorDashboard;
